import React, { useState, useEffect } from 'react';
import { X, CheckCircle, Calendar, Flag, Save, Loader2, User } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EditTaskModal = ({ task, onClose, onSuccess, chatId }) => {
  const { token, user } = useAuth();
  const { isDarkMode } = useTheme();
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [participants, setParticipants] = useState([]);

  const [formData, setFormData] = useState({
    title: task.title || '',
    description: task.description || '',
    priority: task.priority || 'medium',
    dueDate: task.dueDate || '',
    assignedTo: '',
  });

  // Fetch task data from backend + chat participants for assignee dropdown
  useEffect(() => {
    const fetchData = async () => {
      setLoadingData(true);
      try {
        // 1. Fetch actual task data to get assigned_to and source_chat_id
        const taskRes = await axios.get(`${API}/tasks/${task.taskId}`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => null);

        const taskData = taskRes?.data;
        if (taskData) {
          setFormData(prev => ({
            ...prev,
            title: taskData.title || prev.title,
            description: taskData.description || prev.description,
            priority: taskData.priority || prev.priority,
            dueDate: taskData.due_date || prev.dueDate,
            assignedTo: taskData.assigned_to || user?.id || '',
          }));

          // 2. Get participants from the SOURCE chat (where task was created), not the Tasks system chat
          const sourceChatId = taskData.source_chat_id || chatId;
          if (sourceChatId) {
            try {
              const membersRes = await axios.get(`${API}/chats/${sourceChatId}/members`, {
                headers: { Authorization: `Bearer ${token}` }
              });
              if (membersRes.data?.length > 0) {
                setParticipants(membersRes.data);
              }
            } catch {
              // Fallback: show current user + assigned user
              const fallback = [{ id: user.id, display_name: user.display_name || user.username }];
              if (taskData.assigned_to && taskData.assigned_to !== user.id) {
                try {
                  const assigneeRes = await axios.get(`${API}/users/${taskData.assigned_to}/status`, {
                    headers: { Authorization: `Bearer ${token}` }
                  });
                  fallback.push({ id: taskData.assigned_to, display_name: assigneeRes.data?.display_name || 'Assignee' });
                } catch {}
              }
              setParticipants(fallback);
            }
          }
        } else {
          // No task data from API - use what was passed
          setFormData(prev => ({ ...prev, assignedTo: user?.id || '' }));
        }
      } catch (err) {
        console.error('Failed to load task data:', err);
      } finally {
        setLoadingData(false);
      }
    };
    fetchData();
  }, [task.taskId, chatId, token, user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      toast.error('Task title is required');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        title: formData.title.trim(),
        description: formData.description.trim() || undefined,
        priority: formData.priority,
        due_date: formData.dueDate || undefined,
      };
      // Only include assigned_to if it actually changed
      if (formData.assignedTo) {
        payload.assigned_to = formData.assignedTo;
      }

      await axios.put(
        `${API}/tasks/${task.taskId}`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Task updated!');
      onSuccess && onSuccess();
      onClose();
    } catch (error) {
      toast.error('Failed to update task');
    } finally {
      setLoading(false);
    }
  };

  const today = new Date().toISOString().split('T')[0];
  const priorities = [
    { id: 'high', label: 'High', color: 'bg-red-500' },
    { id: 'medium', label: 'Medium', color: 'bg-yellow-500' },
    { id: 'low', label: 'Low', color: 'bg-green-500' },
  ];

  if (loadingData) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="edit-task-modal">
        <div className={`rounded-3xl p-8 max-w-md w-full shadow-2xl flex items-center justify-center ${isDarkMode ? 'bg-gray-900' : 'bg-white'}`}>
          <Loader2 size={24} className="animate-spin text-[#D4AF37]" />
          <span className={`ml-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Loading task...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="edit-task-modal">
      <div className={`rounded-3xl p-6 max-w-md w-full shadow-2xl ${isDarkMode ? 'bg-gray-900' : 'bg-white'}`}>
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isDarkMode ? 'bg-[#D4AF37]/20' : 'bg-[#D4AF37]/10'}`}>
              <CheckCircle size={20} className="text-[#D4AF37]" />
            </div>
            <h3 className={`font-semibold text-lg ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Edit Task</h3>
          </div>
          <button onClick={onClose} className={`h-8 w-8 rounded-full flex items-center justify-center ${isDarkMode ? 'hover:bg-gray-800 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`} data-testid="close-edit-task">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className={`block text-sm font-medium mb-1.5 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Title</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className={`w-full px-3 py-2.5 rounded-xl border text-sm focus:ring-2 focus:ring-[#D4AF37] focus:border-[#D4AF37] outline-none ${isDarkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-200 text-gray-900'}`}
              data-testid="edit-task-title"
            />
          </div>

          <div>
            <label className={`block text-sm font-medium mb-1.5 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={2}
              className={`w-full px-3 py-2.5 rounded-xl border text-sm focus:ring-2 focus:ring-[#D4AF37] focus:border-[#D4AF37] outline-none resize-none ${isDarkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-200 text-gray-900'}`}
              data-testid="edit-task-description"
            />
          </div>

          {/* Assignee */}
          {participants.length > 0 && (
            <div>
              <label className={`block text-sm font-medium mb-1.5 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                <User size={14} className="inline mr-1" />Assign To
              </label>
              <select
                value={formData.assignedTo}
                onChange={(e) => setFormData({ ...formData, assignedTo: e.target.value })}
                className={`w-full px-3 py-2.5 rounded-xl border text-sm focus:ring-2 focus:ring-[#D4AF37] focus:border-[#D4AF37] outline-none ${isDarkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-200 text-gray-900'}`}
                data-testid="edit-task-assignee"
              >
                {participants.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.display_name || p.username}{p.id === user?.id ? ' (You)' : ''}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="flex gap-3">
            <div className="flex-1">
              <label className={`block text-sm font-medium mb-1.5 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                <Calendar size={14} className="inline mr-1" />Due Date
              </label>
              <input
                type="date"
                value={formData.dueDate}
                onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
                min={today}
                className={`w-full px-3 py-2.5 rounded-xl border text-sm focus:ring-2 focus:ring-[#D4AF37] focus:border-[#D4AF37] outline-none ${isDarkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-200 text-gray-900'}`}
                data-testid="edit-task-due-date"
              />
            </div>
            <div className="flex-1">
              <label className={`block text-sm font-medium mb-1.5 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                <Flag size={14} className="inline mr-1" />Priority
              </label>
              <div className="flex gap-1.5">
                {priorities.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => setFormData({ ...formData, priority: p.id })}
                    className={`flex-1 py-2 rounded-lg text-xs font-medium transition-all ${
                      formData.priority === p.id
                        ? `${p.color} text-white shadow-sm`
                        : isDarkMode ? 'bg-gray-800 text-gray-400 hover:bg-gray-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                    data-testid={`edit-task-priority-${p.id}`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading || !formData.title.trim()}
            className="w-full py-3 rounded-xl bg-gradient-to-r from-[#D4AF37] to-[#C5A059] text-white font-medium flex items-center justify-center gap-2 hover:opacity-90 disabled:opacity-50 transition-all"
            data-testid="save-edit-task-btn"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
            {loading ? 'Saving...' : 'Save Changes'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default EditTaskModal;
